// --------------------------------------------------------------
// Created On: 2025-11-06
// Author: Marco Serrano
//
// Last Modified: 2025-12-02
// Modified By: Marco Serrano
//
// Copyright 2025 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import { API } from "../constants/miscellaneous";
import apiRequest from "./api/apiRequest";
import isoUtcAddDays from "../utilities/time/isoUtcAddDays";
import isPacificDaylightTime from "../utilities/time/isPacificDaylightTime";
import addBusinessDays from "./time/addBusinessDays";

// Responsible for getting and formatting the leadtime for a part.
export default async function getLeadTime(
  partNumber: string,
  quantity: string,
  isImpeller: boolean,
  paintColorId: number | null,
  impellerAngle: string | null,
  impellerTrim: string | null,
  onLeadTimeCalculated: (dateAvailable: string) => void,
  onLoadingChange: (loading: boolean) => void,
  onFailedToLoadChange: (failedToLoad: boolean) => void
): Promise<void> {
  const partHasPaintColor: boolean = paintColorId !== null;
  const partHasModifiedImpellerAngle: boolean =
    isImpeller && impellerAngle !== null && !isNaN(parseFloat(impellerAngle)) && parseFloat(impellerAngle) > 0;
  const partHasModifiedImpellerTrim: boolean =
    isImpeller && impellerTrim !== null && !isNaN(parseInt(impellerTrim, 10)) && parseInt(impellerTrim, 10) >= 1;

  onLoadingChange(true);
  const [response, responseBody] = (await apiRequest(`${API}/leadtime`, "POST", {
    partNum: partNumber,
    quantity: quantity,
  })) as [Response, GetLeadTimeResponseBody];

  if (quantity !== "") {
    if (response.ok && responseBody) {
      // The Epicor lead time date uses the following format '2025-02-24T00:00:00.000'.
      // The date is somewhat misleading as the timezone is PST/PDT and the timestamp is always 00:00:00.000.
      // This means that we have to adjust the timestamp if we want to keep track of a UTC date.
      // Since PST/PDT is -8/-7 hours from UTC and the timestamp always represents a start of a date (00:00:00.000),
      // we first determine if we are currently in PST or PDT and add the appropriate amount of hours.
      let epicorDateAvailable = responseBody.dateAvailable;
      if (isPacificDaylightTime(epicorDateAvailable)) {
        epicorDateAvailable = epicorDateAvailable.replace("T00", "T07");
      } else {
        epicorDateAvailable = epicorDateAvailable.replace("T00", "T08");
      }
      calculateLeadTime(
        epicorDateAvailable,
        partHasPaintColor,
        partHasModifiedImpellerAngle,
        partHasModifiedImpellerTrim
      );
      onFailedToLoadChange(false);
    } else {
      onFailedToLoadChange(true);
    }
  }
  onLoadingChange(false);

  // Calculate a part's lead time based on selected modifications.
  async function calculateLeadTime(
    dateAvailable: string,
    partHasPaintColor: boolean,
    partHasModifiedImpellerAngle: boolean,
    partHasModifiedImpellerTrim: boolean
  ) {
    let dateUtc: string = dateAvailable;
    // Always add one day of time to the date available per the sales team's instructions.
    dateUtc = isoUtcAddDays(dateUtc, 1);
    // If a paint color is selected, add 1 day to lead time.
    if (partHasPaintColor) {
      dateUtc = isoUtcAddDays(dateUtc, 1);
    }
    // If there are impeller modifications, add 2 days to lead time.
    if (partHasModifiedImpellerAngle || partHasModifiedImpellerTrim) {
      dateUtc = isoUtcAddDays(dateUtc, 2);
    }
    dateUtc = addBusinessDays(dateUtc);
    onLeadTimeCalculated(dateUtc);
  }
}

interface GetLeadTimeResponseBody {
  dateAvailable: string;
}
